import { lazy, Suspense, useEffect, useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { AlertTriangle, RefreshCw, Wifi, Trash2 } from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import { usePageTracking, trackEvent } from './lib/metrics';
import { subscribe, dismissPopup, getState } from './lib/popupStore';
import { isFeatureEnabled } from './config/features';

const HomePage = lazy(() => import('./pages/HomePage'));
const CommandPalette = lazy(() => import('./components/CommandPalette'));
const HelpCenter = lazy(() => import('./components/HelpCenter'));
const AchievementPopup = lazy(() => import('./components/AchievementPopup'));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const GuidesPage = lazy(() => import('./pages/GuidesPage'));
const GuideArticlePage = lazy(() => import('./pages/GuideArticlePage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsArticlePage = lazy(() => import('./pages/NewsArticlePage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const QuizzesPage = lazy(() => import('./pages/QuizzesPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const SimulationsPage = lazy(() => import('./pages/SimulationsPage'));
const SimulationPage = lazy(() => import('./pages/SimulationPage'));
const ReadLaterPage = lazy(() => import('./pages/ReadLaterPage'));
const AchievementsPage = lazy(() => import('./pages/AchievementsPage'));
const GamesPage = lazy(() => import('./pages/GamesPage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const BrandingPage = lazy(() => import('./pages/BrandingPage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ForumRoutes = lazy(() => import('./forum/ForumRoutes'));

function PageLoading() {
  return (
    <div className="page-loading">
      <div className="page-loading-spinner" />
    </div>
  );
}

const ERROR_SUMMARIES = [
  { pattern: /Failed to fetch|Loading chunk|ChunkLoadError|Importing a module script/i, summary: 'The page could not download a file it needs. This usually happens when your browser has an old or broken copy saved.' },
  { pattern: /QuotaExceededError/i, summary: 'Your browser ran out of storage space for this website.' },
  { pattern: /localStorage|sessionStorage|IndexedDB/i, summary: 'Some data saved on your device got mixed up or broken.' },
  { pattern: /JSON\.parse|Unexpected token/i, summary: 'The page tried to read saved data, but the data was in an unexpected format.' },
  { pattern: /ServiceWorker|service worker|Cache storage/i, summary: 'A background process that manages cached content stopped working correctly.' },
];

function ErrorFallback({ error, resetErrorBoundary }) {
  const [errorCode] = useState(() => 'ERR-' + Math.random().toString(36).slice(2, 7).toUpperCase());
  const [fixing, setFixing] = useState(null);
  const [showTech, setShowTech] = useState(false);

  const msg = error?.message || '';
  const explanation = useMemo(() => {
    const match = ERROR_SUMMARIES.find(e => e.pattern.test(msg));
    return match ? match.summary : 'The page encountered an unexpected problem. This could be a temporary glitch.';
  }, [msg]);

  const fixes = useMemo(() => {
    const f = [];
    if (/Failed to fetch|Loading chunk|ChunkLoadError|Importing a module script/i.test(msg)) {
      f.push('reload');
    }
    if (/QuotaExceededError|localStorage|sessionStorage|IndexedDB|JSON\.parse|Unexpected token/i.test(msg)) {
      f.push('storage');
    }
    if (/ServiceWorker|service worker|Cache storage/i.test(msg)) {
      f.push('cache');
    }
    return f;
  }, [msg]);

  useEffect(() => {
    if (!error) return;
    const payload = {
      error_code: errorCode,
      message: msg || 'Unknown error',
      stack: error.stack || '',
      url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      suggested_fixes: fixes.join(','),
    };
    import('./lib/metrics').then(({ trackEvent }) => {
      trackEvent('error_boundary_caught', payload);
    }).catch(() => {});
  }, [error, errorCode, msg, fixes]);

  function handleClearCache() {
    setFixing('cache');
    if ('caches' in window) {
      caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k))));
    }
    setTimeout(() => setFixing(null), 1500);
  }

  function handleClearStorage() {
    setFixing('storage');
    try {
      const keep = ['cookie-consent'];
      Object.keys(localStorage).forEach(key => {
        if (!keep.includes(key)) localStorage.removeItem(key);
      });
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
    setTimeout(() => setFixing(null), 1500);
  }

  function handleHardReload() {
    setFixing('reload');
    window.location.reload();
  }

  return (
    <main className="container" style={{ paddingTop: '100px', paddingBottom: '100px', textAlign: 'center' }}>
      <div style={{ marginBottom: '16px', color: 'var(--accent)' }}>
        <AlertTriangle size={56} strokeWidth={1.5} />
      </div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '12px' }}>Something went wrong</h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginBottom: '6px' }}>
        An unexpected error occurred. Try refreshing the page or head back to safety.
      </p>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.76rem', fontFamily: 'monospace', marginBottom: 24 }}>
        Reference: {errorCode}
      </p>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
        <button onClick={resetErrorBoundary} className="btn btn-primary">Try Again</button>
        <Link to="/" className="btn btn-secondary">Go Home</Link>
      </div>

      {error && (
        <div
          style={{
            margin: '0 auto', maxWidth: 640, textAlign: 'left',
            borderRadius: 12, overflow: 'hidden',
            border: '1px solid var(--border)',
            background: 'var(--bg-secondary)',
            boxShadow: 'var(--shadow-premium)',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 14px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--bg-primary)',
            }}
          >
            <span
              style={{
                fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: 'var(--text-muted)',
                fontFamily: 'var(--font)',
              }}
            >
              Error Details
            </span>
            <span
              style={{
                fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: 'var(--accent)',
                fontFamily: 'var(--font)',
              }}
            >
              {errorCode}
            </span>
          </div>

          {fixes.length > 0 && (
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--accent-dim)',
              }}
            >
              <div
                style={{
                  fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'var(--accent)',
                  fontFamily: 'var(--font)', marginBottom: 10,
                }}
              >
                Quick Fixes
              </div>
              {fixes.includes('reload') && (
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', marginBottom: fixes.length > 1 ? 8 : 0,
                    background: 'var(--bg-card)', borderRadius: 8,
                    border: '1px solid var(--border)',
                    fontSize: '0.8rem', lineHeight: 1.5, color: 'var(--text-secondary)',
                    fontFamily: 'var(--font)',
                  }}
                >
                  <div
                    style={{
                      width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                      background: 'var(--accent-dim)', color: 'var(--accent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <RefreshCw size={15} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Hard Reload</div>
                    <div style={{ fontSize: '0.75rem' }}>This error can happen when a file failed to load. A hard reload fetches everything fresh from the server.</div>
                  </div>
                  <button
                    onClick={handleHardReload}
                    style={{
                      flexShrink: 0, padding: '6px 14px', borderRadius: 6,
                      border: '1px solid var(--border)', cursor: 'pointer',
                      background: fixing === 'reload' ? 'var(--accent-dim)' : 'var(--bg-card)',
                      color: fixing === 'reload' ? 'var(--accent)' : 'var(--text-secondary)',
                      fontSize: '0.72rem', fontWeight: 600, fontFamily: 'var(--font)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.target.style.background = 'var(--accent-dim)'; e.target.style.color = 'var(--accent)'; e.target.style.borderColor = 'var(--accent)'; }}
                    onMouseLeave={e => { if (fixing !== 'reload') { e.target.style.background = 'var(--bg-card)'; e.target.style.color = 'var(--text-secondary)'; e.target.style.borderColor = 'var(--border)'; } }}
                  >
                    {fixing === 'reload' ? 'Reloading...' : 'Reload'}
                  </button>
                </div>
              )}
              {fixes.includes('cache') && (
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', marginBottom: 8,
                    background: 'var(--bg-card)', borderRadius: 8,
                    border: '1px solid var(--border)',
                    fontSize: '0.8rem', lineHeight: 1.5, color: 'var(--text-secondary)',
                    fontFamily: 'var(--font)',
                  }}
                >
                  <div
                    style={{
                      width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                      background: 'var(--accent-dim)', color: 'var(--accent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Trash2 size={15} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Clear Cache</div>
                    <div style={{ fontSize: '0.75rem' }}>Your browser may be serving outdated files. Clearing the cache removes old data that could be causing conflicts.</div>
                  </div>
                  <button
                    onClick={handleClearCache}
                    style={{
                      flexShrink: 0, padding: '6px 14px', borderRadius: 6,
                      border: '1px solid var(--border)', cursor: 'pointer',
                      background: fixing === 'cache' ? 'var(--accent-dim)' : 'var(--bg-card)',
                      color: fixing === 'cache' ? 'var(--accent)' : 'var(--text-secondary)',
                      fontSize: '0.72rem', fontWeight: 600, fontFamily: 'var(--font)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.target.style.background = 'var(--accent-dim)'; e.target.style.color = 'var(--accent)'; e.target.style.borderColor = 'var(--accent)'; }}
                    onMouseLeave={e => { if (fixing !== 'cache') { e.target.style.background = 'var(--bg-card)'; e.target.style.color = 'var(--text-secondary)'; e.target.style.borderColor = 'var(--border)'; } }}
                  >
                    {fixing === 'cache' ? 'Cleared!' : 'Clear'}
                  </button>
                </div>
              )}
              {fixes.includes('storage') && (
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px',
                    background: 'var(--bg-card)', borderRadius: 8,
                    border: '1px solid var(--border)',
                    fontSize: '0.8rem', lineHeight: 1.5, color: 'var(--text-secondary)',
                    fontFamily: 'var(--font)',
                  }}
                >
                  <div
                    style={{
                      width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                      background: 'var(--accent-dim)', color: 'var(--accent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Wifi size={15} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Reset Local Data</div>
                    <div style={{ fontSize: '0.75rem' }}>Corrupted or overfull local storage can cause errors. Resetting clears saved preferences and cached content.</div>
                  </div>
                  <button
                    onClick={handleClearStorage}
                    style={{
                      flexShrink: 0, padding: '6px 14px', borderRadius: 6,
                      border: '1px solid var(--border)', cursor: 'pointer',
                      background: fixing === 'storage' ? 'var(--accent-dim)' : 'var(--bg-card)',
                      color: fixing === 'storage' ? 'var(--accent)' : 'var(--text-secondary)',
                      fontSize: '0.72rem', fontWeight: 600, fontFamily: 'var(--font)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.target.style.background = 'var(--accent-dim)'; e.target.style.color = 'var(--accent)'; e.target.style.borderColor = 'var(--accent)'; }}
                    onMouseLeave={e => { if (fixing !== 'storage') { e.target.style.background = 'var(--bg-card)'; e.target.style.color = 'var(--text-secondary)'; e.target.style.borderColor = 'var(--border)'; } }}
                  >
                    {fixing === 'storage' ? 'Cleared!' : 'Reset'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div
            style={{
              padding: '16px 20px',
              fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--text-secondary)',
              fontFamily: 'var(--font)',
            }}
          >
            <div
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14,
              }}
            >
              <div
                style={{
                  width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                  background: 'var(--accent-dim)', color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
                }}
              >
                <AlertTriangle size={14} />
              </div>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>What happened</div>
                <div style={{ fontSize: '0.82rem', lineHeight: 1.6 }}>{explanation}</div>
              </div>
            </div>

            {fixes.length === 0 && (
              <div
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 12px', background: 'var(--accent-dim)', borderRadius: 8,
                  border: '1px solid var(--border)',
                  fontSize: '0.82rem', lineHeight: 1.5, color: 'var(--text-secondary)',
                  fontFamily: 'var(--font)',
                }}
              >
                <RefreshCw size={14} style={{ marginTop: 2, flexShrink: 0, color: 'var(--accent)' }} />
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Try Again</div>
                  <div style={{ fontSize: '0.78rem' }}>Click the "Try Again" button above — this kind of error is usually temporary and goes away on its own.</div>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowTech(p => !p)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 14,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font)',
                padding: 0,
              }}
            >
              {showTech ? 'Hide' : 'Show'} technical details
              <span style={{ fontSize: '0.6rem' }}>{showTech ? '▲' : '▼'}</span>
            </button>

            {showTech && (
              <div
                style={{
                  marginTop: 10, padding: '10px 12px', overflow: 'auto', maxHeight: 180,
                  fontSize: '0.75rem', lineHeight: 1.6,
                  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                  color: 'var(--text-muted)',
                  background: 'var(--bg-primary)', borderRadius: 8,
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ marginBottom: 6, color: 'var(--text-secondary)' }}>
                  {error.message || 'No error message'}
                </div>
                {error.stack && (
                  <div style={{ whiteSpace: 'pre-wrap', opacity: 0.7 }}>{error.stack}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

const contentRoutes = [
  { type: 'articles', list: ArticlesPage, detail: ArticlePage },
  { type: 'guides', list: GuidesPage, detail: GuideArticlePage },
  { type: 'news', list: NewsPage, detail: NewsArticlePage },
  { type: 'quizzes', list: QuizzesPage, detail: QuizPage },
];

function AppLayout() {
  usePageTracking();
  const [popup, setPopup] = useState(getState());

  useEffect(() => {
    function onDocClick(e) {
      const link = e.target.closest('a');
      if (!link) return;
      if (link.closest('.article-game-card')) return;
      const href = link.getAttribute('href');
      if (!href || !/^https?:\/\//i.test(href)) return;
      if (link.hostname === window.location.hostname) return;
      trackEvent('outbound_click', {
        url: href,
        link_text: (link.textContent || '').trim().slice(0, 80),
      });
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    const unsub = subscribe(setPopup);
    return unsub;
  }, []);

  return (
    <>
      <Header />
      <Suspense fallback={null}>
        <AchievementPopup
          badgeIds={popup.badgeIds}
          dailyDone={popup.dailyDone}
          dailyLabel={popup.dailyLabel}
          onDismiss={id => dismissPopup(id)}
        />
      </Suspense>
      <div id="main-content">
        <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.reload()}>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              {contentRoutes.flatMap(route => [
                <Route key={route.type} path={`/${route.type}`} element={<route.list />} />,
                <Route key={`${route.type}-slug`} path={`/${route.type}/:slug`} element={<route.detail />} />,
              ])}
              <Route path="/search" element={<SearchPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              {isFeatureEnabled('forum') ? (
                <Route path="/community/*" element={<ForumRoutes />} />
              ) : (
                <Route path="/community" element={<CommunityPage />} />
              )}
              <Route path="/newsletter" element={isFeatureEnabled('newsletter') ? <NewsletterPage /> : <NotFoundPage />} />
              <Route path="/simulations" element={<SimulationsPage />} />
              <Route path="/simulations/:slug" element={<SimulationPage />} />
              <Route path="/read-later" element={<ReadLaterPage />} />
              <Route path="/achievements" element={<AchievementsPage />} />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/games/:slug" element={<GamePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/branding" element={<BrandingPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
      <Footer />
      <CookieConsent />
      <Suspense fallback={null}>
        <CommandPalette />
      </Suspense>
      <Suspense fallback={null}>
        <HelpCenter />
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </HelmetProvider>
  );
}
